import * as _ from 'lodash';
import * as moment from 'moment';
import { Component, Injector, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { charts as chartsCatalog, meters as metersCatalog, formats as formatsCatalog, storage as storageCatalog } from 'src/app/utils/catalog';
import { ChartData, ChartAction } from '../../misc/chart/chart.component';
import { GroupedChartData } from '../../misc/grouped-chart/grouped-chart.component';
import { TabsHeaderSubTab, TabsHeaderTab } from '../../misc/tabs-header/tabs-header.component';
import { LandingSettingsModalComponent } from '../../modals/landing-settings-modal/landing-settings-modal.component';
import { BasePageComponent } from '../base-page/base-page.component';
import { MeterService } from 'src/app/services/meter.service';
import { lastValueFrom } from 'rxjs';
import { ZoneService } from 'src/app/services/zone.service';
import { MeterRecordDataModel } from 'src/app/models/meter-record-data-model';
import { ModelFactory } from 'src/app/models/model-factory';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { ZoneModel } from 'src/app/models/zone-model';
import { DateRangeModalComponent } from '../../modals/date-range-modal/date-range-modal.component';
import { FlowGraphData } from '../../misc/flow-graph/flow-graph.component';
import { ActionDateRangePipe } from 'src/app/pipes/action-date-range.pipe';
import { ActiveActionPipe } from 'src/app/pipes/active-action.pipe';
import { ObjectService } from 'src/app/services/object.service';
import { SessionModel } from 'src/app/models/session-model';
import { AddObjectModalComponent } from '../../modals/add-object-modal/add-object-modal.component';
import { CloudModel } from 'src/app/models/cloud-model';
import { CloudConfigurationModalComponent } from '../../modals/cloud-configuration-modal/cloud-configuration-modal.component';
import { CloudService } from 'src/app/services/cloud.service';
import { MeterPreviewModel } from 'src/app/models/meter-preview-model';
import { MeterControlModel } from 'src/app/models/meter-control-model';

export interface LandingSettings {
  presentation: boolean;
}

export interface FlowGraph {
  label: string;
  data: FlowGraphData;
  dataLoaded: boolean;
}

export interface StatCard {
  label: string;
  units: string;
  value: number;
}

export interface LandingTabsHeaderSubTab extends TabsHeaderSubTab {
  zoneId: string;
  flowGraphs: Array<FlowGraph>;
  stats?: Array<StatCard>;
  charts: Array<{
    label: string;
    type: string;
    settings: {
      type: string;
    };
    dataLoaded: boolean;
    data: Array<ChartData>;
    actions: Array<ChartAction>;
  }>;
}

export interface LandingTabsHeaderTab extends TabsHeaderTab {
  dashboard: boolean;
  subTabs: Array<LandingTabsHeaderSubTab>;
}

export interface ActionData {
  active: boolean;
  activable: boolean;
  type: string;
}

export interface ScaleActionData extends ActionData {
  label: string;
  type: string;
  from: moment.Moment;
  to: moment.Moment;
}

export interface RangeActionData extends ActionData {
  label: string;
  from: moment.Moment;
  to: moment.Moment;
}

export interface ExportActionData extends ActionData {
  zoneId: string;
  meterId: string;
}

export enum ActionType {
  Scale,
  Range,
  Settings,
}

export interface Action extends ChartAction {
  type: ActionType;
  data: ActionData;
}

export interface ScaleAction extends Action {
  data: ScaleActionData;
}

export interface RangeAction extends Action {
  data: RangeActionData;
}

export interface ExportAction extends Action {
  data: ExportActionData;
}

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent extends BasePageComponent {
  @ViewChild('globalScaleTemplate', { static: true }) public globalScaleTemplate: TemplateRef<any>;
  @ViewChild('globalRangeTemplate', { static: true }) public globalRangeTemplate: TemplateRef<any>;
  @ViewChild('chartExportTemplate', { static: true }) public chartExportTemplate: TemplateRef<any>;

  public settings: LandingSettings = {
    presentation: false,
  };
  public cloud: CloudModel = null;
  public tabs: Array<LandingTabsHeaderTab> = [];
  public activeTab: LandingTabsHeaderTab = null;
  public activeSubTab: LandingTabsHeaderSubTab = null;

  public comparisonData: Array<GroupedChartData> = [];
  public consumptionActions: Array<Action> = [];
  public consumptionData: Array<ChartData> = [];
  public productionActions: Array<Action> = [];
  public productionData: Array<ChartData> = [];

  public globalActions: Array<Action> = [];
  public objects: Array<SessionModel> = [];
  public object: SessionModel = null;

  public loading: boolean = false;

  private intervals: Array<ReturnType<typeof setInterval>> = [];
  private activeActionPipe: ActiveActionPipe = new ActiveActionPipe();

  constructor(
    injector: Injector,
    private dialog: MatDialog,
    private meterService: MeterService,
    private objectService: ObjectService,
    private cloudService: CloudService,
    private zoneService: ZoneService
  ) {
    super(injector);
  }

  private static delayedValue(value: any, delay: number = 500): any {
    return new Promise((resolve) =>
      setTimeout(() => {
        resolve(value);
      }, delay)
    );
  }

  private static meterRecordDataToChartData(data: MeterRecordDataModel, scale: string = null): Array<ChartData> {
    let format: string = null;
    switch (scale) {
      case chartsCatalog.scales.day:
        format = formatsCatalog.time.moment;
        break;

      case chartsCatalog.scales.year:
        format = formatsCatalog.month.moment;
        break;

      default:
        format = formatsCatalog.date.momentShort;
        break;
    }
    return data.data.map(
      (d) =>
        <ChartData>{
          name: moment(d.dt).format(format),
          value: _.round(d.value_dif, 1),
          units: data.data_info[0]?.units,
        }
    );
  }

  private static meterPreviewToChartData(preview: MeterPreviewModel): ChartData {
    return {
      name: preview.name,
      value: _.round(preview.value_dif, 1),
      units: preview.units,
    };
  }

  private static getAccumulatedMeterRecordData(recordData: MeterRecordDataModel, scale: string = null) {
    const dataGroups: {
      [key: string]: {
        dt: string;
        value: number;
        value_units: string;
        value_min: number;
        value_min_units: string;
        value_max: number;
        value_max_units: string;
        value_dif: number;
        value_dif_units: string;
      }[];
    } = {};

    recordData.data.forEach((item) => {
      let key;

      switch (scale) {
        case null:
          key = '-';
          break;
        case chartsCatalog.scales.year:
          key = `${moment(item.dt).month()}`;
          break;
        case chartsCatalog.scales.month:
          key = `${moment(item.dt).dayOfYear()}`;
          break;
        case chartsCatalog.scales.week:
          key = `${moment(item.dt).dayOfYear()}`;
          break;
        case chartsCatalog.scales.day:
          key = `${moment(item.dt).hour()}`;
          break;
        default:
          key = item.dt;
          break;
      }

      const dataGroup = dataGroups[key] || [];

      dataGroup.push(item);
      dataGroups[key] = dataGroup;
    });

    const accumulatedData = Object.values(dataGroups).map((dataGroup) => {
      return {
        dt: dataGroup[0]?.dt,
        value: _.sumBy(dataGroup, 'value'),
        value_units: dataGroup[0]?.value_units,
        value_min: _.min(_.map(dataGroup, 'value_min')),
        value_min_units: dataGroup[0]?.value_min_units,
        value_max: _.max(_.map(dataGroup, 'value_max')),
        value_max_units: dataGroup[0]?.value_max_units,
        value_dif: _.sumBy(dataGroup, 'value_dif'),
        value_dif_units: dataGroup[0]?.value_dif_units,
      };
    });

    return ModelFactory.newInstance(MeterRecordDataModel, {
      ...recordData,
      data: accumulatedData,
    });
  }

  private setGlobalActionActive(actionData: ActionData): void {
    if (actionData) {
      this.globalActions = this.globalActions.map((action) => {
        if (!action.data.activable) return action;
        if (action.data === actionData) return { ...action, data: { ...actionData, active: true } };
        return {
          ...action,
          data: {
            ...action.data,
            active: false,
          },
        };
      });
    }
  }

  private initSettings() {
    this.settings = {
      ...this.settings,
      ...this.storageService.get(storageCatalog.keys.settings),
    };
  }

  private initObjects() {
    this.object = this.objectService.getActiveObject();
    this.objects = this.objectService.getObjects();
  }

  private async initCloud() {
    this.cloud = await this.cloudService.getCloud();
  }

  private getDatesByScale(scale: string): { from: moment.Moment; to: moment.Moment } {
    const from = moment().startOf(<moment.unitOfTime.StartOf>scale);
    const to = moment().endOf(<moment.unitOfTime.StartOf>scale);
    return { from, to };
  }

  private initGlobalActions() {
    this.globalActions = [
      ...Object.values(chartsCatalog.scales).map(
        (scale, index) =>
          <ScaleAction>{
            type: ActionType.Scale,
            template: this.globalScaleTemplate,
            data: {
              label: `forms.chartSettings.scales.${scale}`,
              type: scale,
              ...this.getDatesByScale(scale),
              active: index === 0,
              activable: true,
            },
          }
      ),
      <RangeAction>{
        type: ActionType.Range,
        template: this.globalRangeTemplate,
        data: {
          label: 'forms.chartSettings.labels.range',
          ...this.getDatesByScale(chartsCatalog.scales.day),
          active: false,
          activable: true,
        },
      },
    ];
  }

  private getActiveGlobalAction(): Action {
    return this.activeActionPipe.transform(this.globalActions);
  }

  private getActionScale(action: Action): string {
    const isRange = action.type === ActionType.Range;
    const scale = isRange ? chartsCatalog.scales.day : _.get(action, 'data.type');
    return scale;
  }

  private async loadMeterDataForFlowChart(): Promise<FlowGraphData> {
    const data = await this.meterService.getAllControls();
    let units = '';
    let consumption = 0;
    let production = 0;
    let battery = 0;

    for (const meterRecord of data) {
      if (this.cloud.configuration.productionMeterId.includes(meterRecord.id)) {
        production += meterRecord.powertotal;
        if (this.cloud.configuration.consumptionMeterId.includes(meterRecord.id)) {
          consumption += meterRecord.powertotal;
        }
      } else if (_.isEmpty(this.cloud.configuration.consumptionMeterId) || this.cloud.configuration.consumptionMeterId.includes(meterRecord.id)) {
        consumption += meterRecord.powertotal;
      }
      units = meterRecord.powertotal_units;
    }

    return {
      parts: [
        { value: `${_.round(production, 1)} ${units}`, label: 'pages.landing.charts.production' },
        { value: `${_.round(consumption - production, 1)} ${units}`, label: 'pages.landing.charts.distributionConsumption' },
        { value: `${_.round(battery, 1)} ${units}`, label: 'pages.landing.charts.battery' },
      ],
      total: {
        value: `${_.round(consumption, 1)} ${units}`,
        label: 'pages.landing.charts.consumption',
      },
    };
  }

  private async loadMeterDataForChart(zoneId: string, meterId: string) {
    const action = this.getActiveGlobalAction();
    const scale = this.getActionScale(action);

    const data = await this.loadMeterData(zoneId, meterId);
    return LandingPageComponent.meterRecordDataToChartData(data, scale);
  }

  private async loadMeterData(zoneId: string, meterId: string): Promise<MeterRecordDataModel> {
    const action = this.getActiveGlobalAction();
    const scale = this.getActionScale(action);
    const from = moment(_.get(action, 'data.from')).format(formatsCatalog.date.isoMoment);
    const to = moment(_.get(action, 'data.to')).format(formatsCatalog.date.isoMoment);

    const data = LandingPageComponent.getAccumulatedMeterRecordData(
      await this.meterService.getControlRecordData(zoneId, meterId, metersCatalog.valueTypes.energyTotal, from, to),
      scale
    );

    return data;
  }

  private async loadMetersPreview(): Promise<Array<MeterPreviewModel>> {
    const action = this.getActiveGlobalAction();
    const from = moment(_.get(action, 'data.from')).format(formatsCatalog.date.isoMoment);
    const to = moment(_.get(action, 'data.to')).format(formatsCatalog.date.isoMoment);
    const preview = await this.meterService.getAllControlsPreview(metersCatalog.valueTypes.energyTotal, from, to);

    return preview;
  }

  private async getZoneAccumulatedMeterRecordData(
    zoneId: string,
    zones: Array<ZoneModel>,
    meters: Array<MeterControlModel>,
    previews: Array<MeterPreviewModel>
  ): Promise<Array<ChartData>> {
    const data: Array<ChartData> = [];
    const zone = zones.find((z) => z.id === zoneId);
    if (!zone) return data;

    const zoneMeters = meters.filter((meter) => meter.zone_id === zone.zone_id);

    for (const zoneMeter of zoneMeters) {
      const preview = previews.find((p) => p.address === zoneMeter.meter_address);
      if (!preview) continue;
      data.push(LandingPageComponent.meterPreviewToChartData(preview));
    }

    return data;
  }

  private async loadVirtualSubTabData() {
    this.activeSubTab.charts = [];

    if (Array.isArray(this.activeSubTab.zoneId)) {
      const promises = [];
      const chart = {
        label: this.activeTab.label,
        type: 'pages.landing.charts.consumption',
        actions: <Array<ChartAction>>[],
        dataLoaded: false,
        data: <Array<ChartData>>[],
        settings: {
          type: chartsCatalog.types.horizontalBar,
        },
      };
      this.activeSubTab.charts.push(chart);

      const zones = await this.zoneService.getZones();
      const meters = await this.meterService.getAllControls();
      const previews = await this.loadMetersPreview();

      for (const zoneId of this.activeSubTab.zoneId) {
        promises.push(this.getZoneAccumulatedMeterRecordData(zoneId, zones, meters, previews));
      }

      const data = _.reduce(await Promise.all(promises), (acc, res) => acc.concat(res), []);

      chart.data = data;
      chart.dataLoaded = await LandingPageComponent.delayedValue(true);
    }
  }

  private async loadSubTabData() {
    this.activeSubTab.charts = [];
    const meters = await this.meterService.getControls(this.activeSubTab.zoneId);

    meters.forEach(async (meter) => {
      const chart = {
        label: meter.name,
        type: 'pages.landing.charts.consumption',
        actions: [
          {
            template: this.chartExportTemplate,
            data: {
              zoneId: this.activeSubTab.zoneId,
              meterId: meter.id,
            },
          },
        ],
        dataLoaded: false,
        data: <Array<ChartData>>[],
        settings: {
          type: chartsCatalog.types.verticalBar,
        },
      };
      this.activeSubTab.charts.push(chart);

      const data = await this.loadMeterDataForChart(this.activeSubTab.zoneId, meter.id);

      chart.data = data;
      chart.dataLoaded = await LandingPageComponent.delayedValue(true);
    });
  }

  private getAllConsumptionZoneGroups() {
    return _.filter(this.cloud.configuration.zoneGroups, (zoneGroup) => !zoneGroup.dashboard);
  }

  private async refreshFlowGraph(flowGraph: FlowGraph) {
    this.intervals.push(
      setInterval(async () => {
        const flowData = await this.loadMeterDataForFlowChart();
        flowGraph.data = flowData;
      }, 30 * 1000)
    );
  }

  private async clearIntervals() {
    this.intervals.map((interval: ReturnType<typeof setInterval>) => clearInterval(interval));
    this.intervals = [];
  }

  private async loadDashboardTabData() {
    // const meters = await this.meterService.getControls(this.activeSubTab.zoneId);
    this.activeSubTab.charts = [];
    this.activeSubTab.flowGraphs = [];
    const flowGraph = {
      label: 'pages.landing.charts.electricityFlow',
      dataLoaded: false,
      data: <FlowGraphData>{},
    };
    this.activeSubTab.flowGraphs.push(flowGraph);

    const flowData = await this.loadMeterDataForFlowChart();

    flowGraph.data = flowData;
    flowGraph.dataLoaded = await LandingPageComponent.delayedValue(true);

    this.refreshFlowGraph(flowGraph);

    // meters.forEach(async (meter) => {
    //   const chart = {
    //     label: meter.name,
    //     type: 'pages.landing.charts.production',
    //     actions: [
    //       {
    //         template: this.chartSettingsTemplate,
    //         data: {
    //           chartIndex: 0,
    //         },
    //       },
    //     ],
    //     dataLoaded: false,
    //     data: <Array<ChartData>>[],
    //     settings: {
    //       type: chartsCatalog.types.verticalBar,
    //     },
    //   };
    //   this.activeSubTab.charts.push(chart);

    //   const data = await this.loadMeterDataForChart(this.activeSubTab.zoneId, meter.id);

    //   chart.data = data;
    //   chart.dataLoaded = await LandingPageComponent.delayedValue(true);
    // });

    const acuChart = {
      label: this.activeTab.label,
      type: 'pages.landing.charts.consumption',
      actions: <Array<ChartAction>>[],
      dataLoaded: false,
      data: <Array<ChartData>>[],
      settings: {
        type: chartsCatalog.types.horizontalBar,
      },
    };
    this.activeSubTab.charts.push(acuChart);

    const zoneGroups = this.getAllConsumptionZoneGroups();

    const promises: any = [];
    const zones = await this.zoneService.getZones();
    const meters = await this.meterService.getAllControls();
    const previews = await this.loadMetersPreview();

    zoneGroups.forEach(async (zoneGroup) => {
      const tempPromises = [];
      for (const zone of zoneGroup.zones) {
        tempPromises.push(this.getZoneAccumulatedMeterRecordData(zone instanceof ZoneModel ? zone.id : zone, zones, meters, previews));
      }
      promises.push(
        Promise.all(tempPromises).then((res) => {
          const chartData = res.reduce((acc, item) => acc.concat(item), []);
          return {
            name: zoneGroup.name,
            value: _.sumBy(chartData, 'value'),
            units: _.get(chartData, '[0].units', null),
          };
        })
      );
    });

    const data = await Promise.all(promises);

    acuChart.data = data;
    acuChart.dataLoaded = await LandingPageComponent.delayedValue(true);
  }

  private async loadData() {
    try {
      this.loading = true;
      this.clearIntervals();
      if (!this.activeTab) return;
      if (!this.activeSubTab) return;
      if (this.activeTab.dashboard) return this.loadDashboardTabData();
      if (this.activeSubTab.virtual) return this.loadVirtualSubTabData();
      return this.loadSubTabData();
    } finally {
      this.loading = false;
    }
  }

  private async initTabs() {
    const dashboardZoneGroups = _.filter(this.cloud.configuration.zoneGroups, ['dashboard', true]);
    const simpleZoneGroups = _.filter(this.cloud.configuration.zoneGroups, (zoneGroup) => !zoneGroup.dashboard);

    this.tabs = [
      ...dashboardZoneGroups.map((zoneGroup: any) => ({
        label: zoneGroup.name,
        dashboard: true,
        subTabs: [
          {
            label: null,
            virtual: true,
            zoneId: _.get(zoneGroup, `zones[0].id`, null),
            stats: _.get(zoneGroup, 'stats', []).map((stat: any) => ({
              ..._.omit(stat, ['values']),
              value: _.get(stat, `values[${parseInt(moment().format('M')) - 1}]`, 0),
            })),
            charts: [],
            flowGraphs: [],
          },
        ],
      })),
      ...simpleZoneGroups.map((zoneGroup: any) => ({
        label: zoneGroup.name,
        dashboard: false,
        subTabs: [
          {
            label: null,
            virtual: true,
            zoneId: zoneGroup.zones.map((zone: ZoneModel) => zone.id),
            charts: [],
            flowGraphs: [],
          },
          ...zoneGroup.zones.map(
            (zone: ZoneModel) =>
              <TabsHeaderSubTab>{
                label: zone.name,
                zoneId: zone.id,
                virtual: false,
                charts: [],
                flowGraphs: [],
              }
          ),
        ],
      })),
    ];

    this.activeTab = this.tabs[0];
    this.activeSubTab = this.activeTab?.subTabs[0];
  }

  private async init() {
    this.initSettings();
    this.initObjects();
    this.initGlobalActions();
    await this.initCloud();
    await this.initTabs();
    await this.loadData();
  }

  public override ngOnInit() {
    super.ngOnInit();
    this.init();
  }

  public handleTabChange(event: { tab: TabsHeaderTab; tabIndex: number }) {
    this.activeTab = <LandingTabsHeaderTab>event.tab;
    if (!this.activeTab.subTabs || this.activeTab.subTabs.length === 0) {
      this.loadData();
    }
  }

  public handleSubTabChange(event: { tab: TabsHeaderSubTab; tabIndex: number }) {
    this.activeSubTab = <LandingTabsHeaderSubTab>event.tab;
    this.loadData();
  }

  public handleGlobalScaleClick(data: ScaleActionData) {
    const { from, to } = this.getDatesByScale(data.type);
    data.from = from;
    data.to = to;

    this.setGlobalActionActive(data);
    this.loadData();
  }

  public async handleGlobalRangeClick(data: RangeActionData) {
    const result = await lastValueFrom(
      this.dialog
        .open(DateRangeModalComponent, {
          data: {
            form: {
              values: {
                from: data.from,
                to: data.to,
              },
            },
          },
        })
        .afterClosed()
    );
    if (!result) return;

    data.from = result.from;
    data.to = result.to;

    this.setGlobalActionActive(data);
    this.loadData();
  }

  public handleDatePickerStep(step: number = 1) {
    const data = _.get(this.getActiveGlobalAction(), 'data', null);
    if (data) {
      data.from = moment(data.from)
        .add(step, <moment.unitOfTime.DurationConstructor>`${data.type}s`)
        .startOf(<moment.unitOfTime.StartOf>data.type);
      data.to = moment(data.to)
        .add(step, <moment.unitOfTime.DurationConstructor>`${data.type}s`)
        .endOf(<moment.unitOfTime.StartOf>data.type);

      this.setGlobalActionActive(data);
      this.loadData();
    }
  }

  public async handleChartExportClick(data: ExportActionData) {
    const action = this.getActiveGlobalAction();
    const from = moment(_.get(action, 'data.from')).format(formatsCatalog.date.isoMoment);
    const to = moment(_.get(action, 'data.to')).format(formatsCatalog.date.isoMoment);

    await this.meterService.downloadControlRecordData(data.zoneId, data.meterId, metersCatalog.valueTypes.energyTotal, from, to);
  }

  public async handleExportClick() {
    const result = await lastValueFrom(
      this.dialog
        .open(DateRangeModalComponent, {
          data: {
            form: {
              values: {},
            },
          },
        })
        .afterClosed()
    );
    if (!result) return;

    const from = moment(result.from).format(formatsCatalog.date.isoMoment);
    const to = moment(result.to).format(formatsCatalog.date.isoMoment);

    await this.meterService.downloadAllControlsPreview(metersCatalog.valueTypes.energyTotal, from, to);
  }

  public handleLogoutClick() {
    this.authService.logout();
    this.router.navigate([this.routes.login.url]);
  }

  public handlePresentationClick() {
    this.settings.presentation = !this.settings.presentation;
    this.storageService.put(storageCatalog.keys.settings, this.settings);
  }

  public handleObjectClick(object: SessionModel) {
    this.objectService.setActiveObject(object);
    this.init();
  }

  public async handleAddObjectClick() {
    const result = await lastValueFrom(this.dialog.open(AddObjectModalComponent).afterClosed());
    if (!result) return;

    this.initObjects();
  }

  public handleObjectDeleteClick(object: SessionModel) {
    this.objectService.removeObject(object);
    this.initObjects();
  }

  public async handleCloudConfigurationClick() {
    const result = await lastValueFrom(
      this.dialog
        .open(CloudConfigurationModalComponent, {
          minWidth: 1000,
          data: {
            form: {
              values: this.cloud?.configuration,
            },
          },
        })
        .afterClosed()
    );

    if (!result) return;

    this.init();
  }
}
